import { NgZone } from '@angular/core';
import { DeepLinkService, linkPathOf } from '../shared/deep-link.service';

/**
 * Back closes a popup. A popup / 📈 modal / footer overlay (a full-screen sheet on phones, a centred card
 * from 576 px) opened by a tap gets a history entry of its own:
 *
 * - {@link pushSheetEntry} marks the current entry as the one the sheet `id` was opened from (FROM) and
 *   pushes a copy of it marked as the sheet's entry (MARK); the app's link write then turns that copy into
 *   the sheet's URL (DeepLinkService replaces with history.state, so the marks stay).
 * - Back (or ✕ via {@link leaveSheetEntry}) returns to the entry it was opened from. That entry still holds
 *   the link of before the tap, so what changed meanwhile in keys that outlive the view (a tab's range,
 *   "klima.humidity" …) is written into it before DeepLinkService reads it; external$ then closes the sheet
 *   and every component re-applies the link, as for any Back.
 *
 * View paths ("heizung/boiler") and other ids ("overlay=changelog") both work; only views have a state to carry.
 */
const MARK = 'dashSheet';
const FROM = 'dashSheetFrom';

let link: DeepLinkService | null = null;
let zone: NgZone | null = null;
/** The sheet ✕ is going back over (its view is already closed in the link). */
let leaving: string | null = null;

/** Before a tap opens the sheet `id` (call it before the link write that opens its view). */
export function pushSheetEntry(id: string, deepLink: DeepLinkService, ngZone: NgZone): void {
  link = deepLink;
  zone = ngZone;
  link.href();   // pending link writes belong to the entry being left
  const state = { ...(history.state ?? {}) };
  delete state[FROM];
  try {
    history.replaceState({ ...state, [FROM]: id }, '');
    history.pushState({ ...state, [MARK]: id }, '', location.href);
  } catch { /* Safari's history rate limit: the sheet just replaces, as on desktop */ }
}

/** After ✕ closed the sheet `id` (and its view in the link): if the current entry is the one its tap
 *  pushed (also after Forward back to it), go back over it, so Back does not reopen it. */
export function leaveSheetEntry(id: string): void {
  if (history.state?.[MARK] !== id) return;
  leaving = id;
  history.back();
}

/** Back onto the entry a sheet was opened from, while that sheet is open (or ✕ closed it): carry over. */
function onPopState(): void {
  const id: unknown = history.state?.[FROM];
  const closing = leaving;
  leaving = null;
  const lk = link;
  if (!lk || !zone || typeof id !== 'string' || !(closing === id || lk.isOpen(id))) return;
  zone.run(() => {
    // the path this entry had (e.g. "knx/weather"; an old link in its new form), with the keys of now
    const path = linkPathOf(location.hash);
    lk.closeView(id);
    if (path) lk.openView(path);
    // into this entry now, announced like a navigation (DeepLinkService's own popstate listener then
    // finds the URL it wrote and skips it)
    lk.commitAndAnnounce();
  });
}

// Added on import, i.e. before DeepLinkService adds its own (created at bootstrap): listeners of an event
// run in the order they were added, so this one runs first.
window.addEventListener('popstate', onPopState);
